import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

function AttendeesDirectory() {
  const [attendees, setAttendees] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [totalCount, setTotalCount] = useState(0);
  // Helper: initials fallback if no avatar
  const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  // chsck 
  // Helper: avatar component
  const Avatar = ({ attendee, size = 40 }) => {
    const url = attendee?.avatar_url;
    const initials = getInitials(attendee?.name || "");

    return (
      <div
        className="rounded-full overflow-hidden bg-gray-100 border flex items-center justify-center shrink-0"
        style={{ width: size, height: size }}
      >
        {url ? (
          <img
            src={url}
            alt={attendee?.name || "Avatar"}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-gray-600 font-semibold" style={{ fontSize: Math.max(12, size / 2.5) }}>
            {initials}
          </span>
        )}
      </div>
    );
  };


  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  useEffect(() => {
    fetchAttendees(debouncedQuery, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, page]);

  async function fetchAttendees(queryText, pageNumber) {
    setLoading(true);
    setError(null);

    const from = (pageNumber - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("users")
      .select("name, email, company, role, public_profile, clubs, tech_interests, bio, links, avatar_url", { count: 'exact' })
      .eq('public_profile', true)
      .order('name', { ascending: true })
      .range(from, to);

    if (queryText) {
      query = query.or(`name.ilike.%${queryText}%,company.ilike.%${queryText}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      setError("Failed to load attendees. Please try again.");
      setAttendees([]);
      setTotalCount(0);
    } else {
      setAttendees((data || []).filter(a => a.public_profile === true));
      setTotalCount(count || 0);
    }

    setLoading(false);
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-semibold mb-6">Attendees Directory</h1>

      <input
        type="text"
        placeholder="Search by name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full border border-gray-300 rounded-md p-2 mb-6 focus:outline-none focus:ring-2 focus:ring-blue-400"
      />

      {loading ? (
        <p className="text-gray-600">Loading attendees...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : attendees.length === 0 ? (
        <p className="text-gray-500">No attendees found.</p>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="grid gap-3 md:hidden">
            {attendees.map((attendee, idx) => (
              <div
                key={attendee.email || idx}
                className="p-4 border border-gray-200 rounded-lg bg-white shadow-sm"
                onClick={() => setSelectedAttendee(attendee)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar attendee={attendee} size={36} />
                    <div className="font-semibold text-gray-900">{attendee.name}</div>
                  </div>
                  <span className="text-xs text-gray-500">{attendee.role || "Attendee"}</span>
                </div>

                <div className="text-sm text-gray-700 mt-1">
                  {attendee.company || 'School / Affiliation not provided'}
                </div>
                <div className="text-xs text-gray-500">{attendee.email}</div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="overflow-x-auto hidden md:block">
            <table className="min-w-full border border-gray-200 bg-white rounded-lg shadow-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">School / Affiliation</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                </tr>
              </thead>
          
              <tbody className="divide-y divide-gray-200">
                {attendees.map((attendee, index) => (
                  <tr
                    key={attendee.email || index}
                    className="hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => setSelectedAttendee(attendee)}
                  >
                    <td className="px-6 py-3 text-gray-900 font-medium">
                      <div className="flex items-center gap-3">
                        <Avatar attendee={attendee} size={32} />
                        <span className="underline-offset-2 hover:underline">{attendee.name}</span>
                      </div>
                    </td>

                    <td className="px-6 py-3 text-gray-700">
                      {attendee.company || "-"}
                    </td>
                    <td className="px-6 py-3 text-gray-600">
                      {attendee.email}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Page {page} of {Math.max(1, Math.ceil(totalCount / pageSize))}
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(totalCount / pageSize)}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {selectedAttendee && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 relative">
            <button
              onClick={() => setSelectedAttendee(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              ×
            </button>
            <div className="flex items-center gap-3 mb-3">
              <Avatar attendee={selectedAttendee} size={48} />
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">{selectedAttendee.name}</h3>
                <p className="text-sm text-gray-500">{selectedAttendee.email}</p>
              </div>
            </div>
            
            {selectedAttendee.role && (
              <p className="text-sm text-gray-600 mb-3">Role: {selectedAttendee.role}</p>
            )}
            {selectedAttendee.links && (
              <p className="text-sm text-primary-600 mb-4">
                <a
                  href={/^https?:\/\//i.test(selectedAttendee.links) ? selectedAttendee.links : `https://${selectedAttendee.links}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {selectedAttendee.links}
                </a>
              </p>
            )}

            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <p className="font-medium text-gray-900">School / Affiliation</p>
                <p className="text-gray-700">{selectedAttendee.company || 'Not provided'}</p>
              </div>
              <div>
                <p className="font-medium text-gray-900">Clubs</p>
                {Array.isArray(selectedAttendee.clubs) && selectedAttendee.clubs.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedAttendee.clubs.map((club) => (
                      <span key={club} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {club}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">Not provided</p>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">Tech Interests</p>
                {Array.isArray(selectedAttendee.tech_interests) && selectedAttendee.tech_interests.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedAttendee.tech_interests.map((tech) => (
                      <span key={tech} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {tech}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">Not provided</p>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900">Bio</p>
                <p className="text-gray-700 break-words">{selectedAttendee.bio || 'Not provided'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AttendeesDirectory;
